import { RolodexEntryType } from 'types'
import { DataProcessingConfigSchema } from 'types/timeSeries/dataProcessing'
import { z } from 'zod'

const TagsSchema = z.record(z.string(), z.string().nullish())

const OptionalTimestamps = z.object({
  updated_at: z.coerce.date().nullish(),
  created_at: z.coerce.date().nullish(),
  last_updated_by: z.string().nullish(),
})

export const ChartTimeUnitSchema = z.enum([
  'seconds',
  'minutes',
  'hours',
  'days',
  'weeks',
  'months',
])
export type ChartTimeUnit = z.infer<typeof ChartTimeUnitSchema>

export const ChartTimeUnit = {
  SECONDS: 'seconds',
  MINUTES: 'minutes',
  HOURS: 'hours',
  DAYS: 'days',
  WEEKS: 'weeks',
  MONTHS: 'months',
} as const satisfies Record<string, ChartTimeUnit>

export const YAxisModeSchema = z.enum(['single', 'multiple'])
export type YAxisMode = z.infer<typeof YAxisModeSchema>

export const SourceTypeSchema = z.enum(['sensor', 'derivedValue'])
export type SourceType = z.infer<typeof SourceTypeSchema>

export const YAxisRangeSchema = z
  .object({
    min: z.number(),
    max: z.number(),
  })
  .refine((v) => v.max >= v.min, {
    message: 'yAxisRange.max must be ≥ min',
    path: ['max'],
  })

export const SourceConfigSchema = z
  .object({
    type: SourceTypeSchema,
    source_id: z.string(),
    color: z.string(),
    hidden: z.boolean().nullish(),
    y_axis_range: YAxisRangeSchema.nullish(),
  })
  .transform((data) => {
    return {
      type: data.type,
      sourceId: data.source_id,
      color: data.color,
      hidden: data.hidden,
      yAxisRange: data.y_axis_range,
    }
  })

export const DateModeSchema = z.enum(['absolute', 'relative'])
export type DateMode = z.infer<typeof DateModeSchema>

const AbsoluteDateConfigSchema = z
  .object({
    mode: z.literal('absolute'),
    start_date: z.coerce.date(),
    end_date: z.coerce.date(),
  })
  .transform((data) => {
    return {
      mode: data.mode,
      startDate: data.start_date,
      endDate: data.end_date,
    }
  })

const RelativeLookbackSchema = z.object({
  value: z.number(),
  unit: ChartTimeUnitSchema,
})

const RelativeDateConfigSchema = z
  .object({
    mode: z.literal('relative'),
    start_date_lookback: RelativeLookbackSchema,
    end_date_lookback: RelativeLookbackSchema.nullish(),
    update_cadence_seconds: z.number().int().positive(),
  })
  .transform((data) => {
    return {
      mode: data.mode,
      startDateLookback: data.start_date_lookback,
      endDateLookback: data.end_date_lookback,
      updateCadenceSeconds: data.update_cadence_seconds,
    }
  })

export const DateConfigSchema = z.discriminatedUnion('mode', [
  AbsoluteDateConfigSchema,
  RelativeDateConfigSchema,
])

// ---------- chart data ----------
// Downstream: requires upstreamChartId; others optional
export const DownstreamChartSchema = z
  .object({
    name: z.string().nullish(),
    description: z.string().nullish(),
    data_processing_configs: z.array(DataProcessingConfigSchema).nullish(),
    upstream_chart_id: z.string(),
    y_axis_mode: YAxisModeSchema.nullish(),
    source_configs: z.array(SourceConfigSchema).nullish(),
    date_config: DateConfigSchema.nullish(),
  })
  .transform((data) => {
    return {
      name: data.name,
      description: data.description,
      dataProcessingConfigs: data.data_processing_configs,
      upstreamChartId: data.upstream_chart_id,
      yAxisMode: data.y_axis_mode,
      sourceConfigs: data.source_configs,
      dateConfig: data.date_config,
    }
  })

export const StandaloneChartSchema = z
  .object({
    name: z.string().nullish(),
    description: z.string().nullish(),
    data_processing_configs: z.array(DataProcessingConfigSchema).nullish(),
    y_axis_mode: YAxisModeSchema,
    source_configs: z.array(SourceConfigSchema),
    date_config: DateConfigSchema,
  })
  .transform((data) => {
    return {
      name: data.name,
      description: data.description,
      dataProcessingConfigs: data.data_processing_configs,
      yAxisMode: data.y_axis_mode,
      sourceConfigs: data.source_configs,
      dateConfig: data.date_config,
    }
  })

export const ChartDataSchema = z.union([
  DownstreamChartSchema,
  StandaloneChartSchema,
])

// ---------- entry wrappers ----------
export const ChartEntryJSONSchema = z
  .object({
    id: z.string(),
    type: z.literal(RolodexEntryType.CHART),
    data: ChartDataSchema,
    tags: TagsSchema,
    ...OptionalTimestamps.shape,
  })
  .transform((schema) => ({
    id: schema.id,
    type: schema.type,
    data: schema.data,
    tags: schema.tags,
    ...(schema.updated_at && { updatedAt: schema.updated_at }),
    ...(schema.created_at && { createdAt: schema.created_at }),
    ...(schema.last_updated_by && {
      lastUpdatedBy: schema.last_updated_by,
    }),
  }))

export const ChartEntrySchema = z.object({
  entry: ChartEntryJSONSchema,
})

// ---------- inferred types ----------
export type YAxisRange = z.infer<typeof YAxisRangeSchema>
export type SourceConfig = z.infer<typeof SourceConfigSchema>
export type DateConfig = z.infer<typeof DateConfigSchema>
export type DownstreamChart = z.infer<typeof DownstreamChartSchema>
export type StandaloneChart = z.infer<typeof StandaloneChartSchema>
export type ChartData = z.infer<typeof ChartDataSchema>
export type ChartEntryJSON = z.infer<typeof ChartEntryJSONSchema>
export type ChartEntry = z.infer<typeof ChartEntrySchema>
